import type { HTMLAttributes, LiHTMLAttributes, ReactNode } from 'react'

// Universal search option interface
export interface SearchOption {
  value: string
  label: string
  subtext?: string
  rightContent?: ReactNode
  metadata?: Record<string, unknown>
  labelIcon?: ReactNode
}

// Main component props
export interface SearchInputProps {
  // Core functionality
  value: string
  onChange: (value: string) => void
  onSelect?: (option: SearchOption) => void

  // Search behavior
  onSearch: (term: string) => SearchOption[] | Promise<SearchOption[]>
  debounceMs?: number
  minSearchLength?: number

  // UI customization
  placeholder?: string
  size?: SearchInputSize
  disabled?: boolean
  required?: boolean

  // Loading states
  isLoading?: boolean
  loadingMessage?: string
  emptyMessage?: string
  errorMessage?: string
  shortSearchMessage?: string

  // Styling
  className?: string // Input component styles
  dropdownClassName?: string
  containerClassName?: string

  // Custom rendering
  renderOption?: (
    option: SearchOption,
    context: { isFocused: boolean },
  ) => ReactNode

  // Advanced options
  maxResults?: number
  disableDropdown?: boolean // Disable dropdown, allow pure text input
  showOptionsOnFocus?: boolean // Show options when input is focused, even if empty
  clearOnSelect?: boolean
  onDropdownOpen?: () => void // Callback when dropdown opens
}

// Styled component prop interfaces
export interface SearchContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export interface SearchDropdownProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export interface SearchResultsListProps
  extends HTMLAttributes<HTMLUListElement> {
  children: ReactNode
}

export interface SearchResultItemProps extends LiHTMLAttributes<HTMLLIElement> {
  children: ReactNode
  isFocused?: boolean
}

export interface SearchResultMessageProps
  extends LiHTMLAttributes<HTMLLIElement> {
  children: ReactNode
  variant?: SearchResultMessageVariant
}

// Enums
export enum SearchInputSize {
  SMALL = 'small',
  LARGE = 'large',
}

export enum SearchResultMessageVariant {
  LOADING = 'loading',
  ERROR = 'error',
  EMPTY = 'empty',
}
